import React, { useState, useEffect } from 'react';
import BoxWrapper from '../../../../components/ui/boxWrapper';
import axios from "../../../../axios-order";
import { Snackbar, Alert, IconButton } from '@mui/material';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";


function GSTMapping() {
    const [mappingData, setMappingData] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [expandedRows, setExpandedRows] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    // editedData: { [childKey]: [ { subheader: '', mappedColumn: '' }, ... ] }
    const [editedData, setEditedData] = useState({});

    // Fetch GST Mapping Repository data
    const getGSTMappingData = () => {
        const payload = {};
        setIsLoading(true);
        axios
            .post("/get_all_gst_mapping_details", payload)
            .then((res) => {
                const records = res.data.records;
                const mappingList = Object.keys(records).map(parentKey => {
                    const parentData = records[parentKey];
                    const invoiceTypes = Object.keys(parentData).map(key => ({
                        name: key,
                        details: parentData[key]
                    }));
                    return {
                        parent: parentKey.toUpperCase(),
                        children: invoiceTypes
                    };
                });

                setMappingData(mappingList);
                // Initialize editedData from API response
                const initialEdited = {};
                mappingList.forEach((parentItem, parentIndex) => {
                    parentItem.children.forEach((child, childIndex) => {
                        const childKey = `${parentIndex}-${childIndex}`;
                        const subheaders = child.details?.sub_headers || [];
                        const mappedColumns = child.details?.mapped_columns || [];
                        const maxLen = Math.max(subheaders.length, mappedColumns.length, 1);
                        initialEdited[childKey] = Array.from({ length: maxLen }, (_, rowIdx) => ({
                            subheader: subheaders[rowIdx] ?? '',
                            mappedColumn: mappedColumns[rowIdx] ?? ''
                        }));
                    });
                });
                setEditedData(initialEdited);
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setSnackbarMessage('Error fetching GST mapping data');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getGSTMappingData();
    }, []);

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const toggleRow = (key) => {
        setExpandedRows(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Handle cell edit
    const handleCellChange = (childKey, rowIdx, field, value) => {
        setEditedData(prev => {
            const rows = [...(prev[childKey] || [])];
            rows[rowIdx] = { ...rows[rowIdx], [field]: value };
            return { ...prev, [childKey]: rows };
        });
    };

    // Add a new empty row for a child
    const handleAddRow = (childKey) => {
        setEditedData(prev => {
            const rows = [...(prev[childKey] || [])];
            rows.push({ subheader: '', mappedColumn: '' });
            return { ...prev, [childKey]: rows };
        });
    };

    // Remove a row
    const handleRemoveRow = (childKey, rowIdx) => {
        setEditedData(prev => {
            const rows = [...(prev[childKey] || [])];
            rows.splice(rowIdx, 1);
            return { ...prev, [childKey]: rows };
        });
    };

    const styles = {
        mappingTable: {
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #d0d7e2',
            borderRadius: '8px',
            overflow: 'hidden',
            fontSize: '13px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
        },
        tableHeader: {
            backgroundColor: '#e8edf5',
            color: '#003366',
            fontWeight: '700',
            fontSize: '13px',
            padding: '10px 14px',
            borderBottom: '2px solid #c0cde0',
            borderRight: '1px solid #d0d7e2',
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        tableHeaderLast: {
            backgroundColor: '#e8edf5',
            color: '#003366',
            fontWeight: '700',
            fontSize: '13px',
            padding: '10px 14px',
            borderBottom: '2px solid #c0cde0',
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        tableCell: {
            padding: '6px 10px',
            borderBottom: '1px solid #eaeef4',
            borderRight: '1px solid #eaeef4',
            verticalAlign: 'middle',
            textAlign: 'center'
        },
        tableCellLast: {
            padding: '6px 10px',
            borderBottom: '1px solid #eaeef4',
            verticalAlign: 'middle',
            textAlign: 'center'
        },
        editableInput: {
            width: '100%',
            padding: '6px 10px',
            border: '1px solid #d0d7e2',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#333',
            backgroundColor: '#fff',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s'
        },
        actionCell: {
            padding: '6px 8px',
            borderBottom: '1px solid #eaeef4',
            verticalAlign: 'middle',
            textAlign: 'center',
            width: '40px'
        },
        addRowBtn: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '8px',
            cursor: 'pointer',
            color: '#003366',
            fontSize: '12px',
            fontWeight: '600',
            border: 'none',
            background: 'transparent',
            padding: '4px 2px'
        }
    };

    // Renders the expanded mapping table for a child
    const renderMappingTable = (childKey) => {
        const rows = editedData[childKey] || [];

        return (
            <div style={{ padding: '12px 20px 16px 20px', backgroundColor: '#f9fafb' }}>
                <table style={{ ...styles.mappingTable, tableLayout: 'fixed' }}>
                    <colgroup>
                        <col style={{ width: '50%' }} />
                        <col style={{ width: '50%' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={styles.tableHeader}>Sub Headers</th>
                            <th style={styles.tableHeaderLast}>Mapped Columns</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIdx) => (
                            <tr
                                key={rowIdx}
                                style={{ backgroundColor: rowIdx % 2 === 0 ? '#ffffff' : '#f5f7fb' }}
                            >
                                <td style={styles.tableCell}>
                                    <input
                                        type="text"
                                        value={row.subheader}
                                        onChange={(e) => handleCellChange(childKey, rowIdx, 'subheader', e.target.value)}
                                        placeholder="Enter sub header"
                                        style={styles.editableInput}
                                        onFocus={(e) => e.target.style.borderColor = '#003366'}
                                        onBlur={(e) => e.target.style.borderColor = '#d0d7e2'}
                                    />
                                </td>
                                <td style={styles.tableCellLast}>
                                    <input
                                        type="text"
                                        value={row.mappedColumn}
                                        onChange={(e) => handleCellChange(childKey, rowIdx, 'mappedColumn', e.target.value)}
                                        placeholder="Enter mapped column"
                                        style={styles.editableInput}
                                        onFocus={(e) => e.target.style.borderColor = '#003366'}
                                        onBlur={(e) => e.target.style.borderColor = '#d0d7e2'}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const tableBodyData = () => {
        if (mappingData && mappingData.length > 0) {
            return mappingData.map((item, i) => (
                <React.Fragment key={i}>
                    {/* GSTR2B Parent Row */}
                    <tr>
                        <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() => toggleRow(i)}
                                    style={{ padding: '4px' }}
                                >
                                    {expandedRows[i] ? (
                                        <DoNotDisturbOnIcon style={{ color: "black", fontSize: '22px' }} />
                                    ) : (
                                        <AddCircleIcon style={{ color: "black", fontSize: '22px' }} />
                                    )}
                                </IconButton>
                                <span style={{ fontWeight: '700', fontSize: '16px', color: '#003366' }}>
                                    {item.parent}
                                </span>
                            </div>
                        </td>
                    </tr>

                    {/* Child Rows - Invoice Types */}
                    {expandedRows[i] && item.children.map((child, childIndex) => {
                        const childKey = `${i}-${childIndex}`;
                        return (
                            <React.Fragment key={`child-${childIndex}`}>
                                <tr>
                                    <td style={{ padding: '10px 10px 10px 50px', backgroundColor: '#f8f9fa' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <IconButton
                                                aria-label="expand child row"
                                                size="small"
                                                onClick={() => toggleRow(childKey)}
                                                style={{ padding: '4px' }}
                                            >
                                                {expandedRows[childKey] ? (
                                                    <DoNotDisturbOnIcon style={{ color: "black", fontSize: '18px' }} />
                                                ) : (
                                                    <AddCircleIcon style={{ color: "black", fontSize: '18px' }} />
                                                )}
                                            </IconButton>
                                            <span style={{ fontWeight: '600', fontSize: '15px' }}>{child.name}</span>
                                        </div>
                                    </td>
                                </tr>

                                {/* Expanded Child Content - Editable Mapping Table */}
                                {expandedRows[childKey] && (
                                    <tr>
                                        <td style={{ padding: '0 20px 0 70px', backgroundColor: '#ffffff' }}>
                                            {renderMappingTable(childKey)}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </React.Fragment>
            ));
        } else {
            return (
                <tr>
                    <td style={{ textAlign: "center", padding: '20px', color: isLoading ? 'black' : 'red' }} colSpan={1}>
                        {isLoading ? 'Loading... Please wait' : 'No Data Found!'}
                    </td>
                </tr>
            );
        }
    };

    return (
        <>
            <div style={{ margin: "30px" }}>
                <BoxWrapper boxTitle="GST Mapping Repository">
                    <div className="responsive-table-tds-threshold">
                        <table className="custome-table">
                            <tbody>
                                {tableBodyData()}
                            </tbody>
                        </table>
                    </div>
                </BoxWrapper>
            </div>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

export default GSTMapping;
